import React from "react";

import { FormikErrors } from "formik";
import Button from "../../../../components/form/Button";
import Input from "../../../../components/form/Input";
import { GoogleDocsDestination } from "../../../../models";

interface GoogleDocsProps {
  values: GoogleDocsDestination;
  errors: FormikErrors<GoogleDocsDestination>;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  isEdit: boolean;
  onConnect: () => void;
  isConnecting: boolean;
  onTest: () => void;
  isTesting: boolean;
}

// Google Docs has no typed secret: the account is connected through Google's own consent screen.
const GoogleDocs: React.FC<GoogleDocsProps> = ({
  values,
  errors,
  setFieldValue,
  isEdit,
  onConnect,
  isConnecting,
  onTest,
  isTesting,
}) => {
  const { googleEmail, folderId, needsReconnect, folderName } =
    values.destination;
  const isConnected = Boolean(googleEmail) && !needsReconnect;

  return (
    <div className="py-4 px-4 sm:px-6 flex flex-col gap-6 mb-4">
      <div className="flex flex-col md:flex-row gap-6">
        <Input
          id="name"
          label="Destination Name"
          name="name"
          type="text"
          required
          placeholder="Name your destination"
          className="max-w-md"
          tooltip="Name your destination (e.g., Client Docs)"
          value={values.name}
          errorMessage={errors?.name}
          errorPosition="bottom-left"
          onChange={(e: any) => setFieldValue("name", e.target.value)}
        />
        <Input
          id="folderName"
          label="Google Drive Folder"
          name="destination.folderName"
          type="text"
          placeholder="ArticleX"
          className="max-w-md"
          tooltip="ArticleX creates this folder in the connected Google Drive the first time you connect. Articles are saved inside it as Google Docs."
          value={values.destination.folderName}
          disabled={isEdit && Boolean(folderId)}
          errorMessage={errors?.destination?.folderName}
          errorPosition="bottom-left"
          onChange={(e: any) =>
            setFieldValue("destination.folderName", e.target.value)
          }
        />
      </div>

      <div className="flex flex-col gap-3 max-w-2xl">
        <div className="text-sm font-medium text-gray-700">Google account</div>

        {!isEdit && (
          <div className="text-sm text-gray-500">
            You will be sent to Google to connect the account whose Drive will
            receive the articles. The destination is saved only once Google has
            connected: if anything fails or you cancel, nothing is saved.
            ArticleX only gets access to the files and folder it creates.
          </div>
        )}

        {isEdit && needsReconnect && (
          <div className="text-sm text-red-600 border border-red-200 bg-red-50 rounded p-3">
            Google access was revoked or has expired, so new articles are not
            being saved. Reconnect the Google account to resume.
          </div>
        )}

        {isEdit && isConnected && (
          <div className="text-sm text-gray-700 flex flex-col gap-1">
            <div>
              Connected as <span className="font-medium">{googleEmail}</span>
            </div>
            {folderId && (
              <a
                className="text-primary w-max"
                href={`https://drive.google.com/drive/folders/${folderId}`}
                target="_blank"
                rel="noreferrer"
              >
                Open the “{folderName}” folder in Google Drive
              </a>
            )}
          </div>
        )}

        {isEdit && !isConnected && !needsReconnect && (
          <div className="text-sm text-gray-500">
            No Google account is connected yet.
          </div>
        )}

        {isEdit && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="primary"
              className="min-w-[4rem]"
              onClick={onConnect}
              loading={isConnecting}
            >
              {googleEmail || needsReconnect
                ? "Reconnect Google account"
                : "Connect Google account"}
            </Button>
            {googleEmail && (
              <Button
                type="button"
                variant="light-primary"
                onClick={onTest}
                loading={isTesting}
              >
                Test connection
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleDocs;
